import React from "react";
import requirementStyles from "./sol-requirements.module.css";
import { useT } from "../../i18n";
import { REVIEW_TABS, type ReviewTabId } from "./model";
import styles from "./email-review.module.css";

export function ReviewTabs({
  activeTab,
  onTabChange,
  editing,
  requiredCount,
}: {
  activeTab: ReviewTabId;
  onTabChange: (tab: ReviewTabId) => void;
  editing: boolean;
  requiredCount?: number | null;
}) {
  const t = useT();
  const refs = React.useRef<Array<HTMLButtonElement | null>>([]);

  const keepVisible = React.useCallback((index: number) => {
    refs.current[index]?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  }, []);

  React.useLayoutEffect(() => {
    keepVisible(REVIEW_TABS.indexOf(activeTab));
  }, [activeTab, keepVisible]);

  const selectAt = (index: number) => {
    const normalizedIndex = (index + REVIEW_TABS.length) % REVIEW_TABS.length;
    const next = REVIEW_TABS[normalizedIndex];
    if (editing && next !== "draft") {
      refs.current[0]?.focus();
      return;
    }
    onTabChange(next);
    refs.current[normalizedIndex]?.focus();
    keepVisible(normalizedIndex);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key === "ArrowRight") {
      event.preventDefault();
      selectAt(index + 1);
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      selectAt(index - 1);
    } else if (event.key === "Home") {
      event.preventDefault();
      selectAt(0);
    } else if (event.key === "End") {
      event.preventDefault();
      selectAt(REVIEW_TABS.length - 1);
    }
  };

  return (
    <div
      className={styles.tabs}
      role="tablist"
      aria-label={t("ws.review.details")}
      aria-orientation="horizontal"
    >
      {REVIEW_TABS.map((tab, index) => {
        const active = tab === activeTab;
        const locked = editing && tab !== "draft";
        return (
          <button
            type="button"
            role="tab"
            id={`review-tab-${tab}`}
            aria-controls={active ? `review-panel-${tab}` : undefined}
            aria-selected={active}
            aria-disabled={locked || undefined}
            disabled={locked}
            tabIndex={active ? 0 : -1}
            key={tab}
            ref={(node) => { refs.current[index] = node; }}
            className={active ? `${styles.tab} ${styles.tabActive}` : styles.tab}
            onKeyDown={(event) => onKeyDown(event, index)}
            onClick={() => { if (!locked) onTabChange(tab); }}
          >
            {t(`ws.review.tab.${tab}`)}
            {tab === "facts" && requiredCount != null && requiredCount > 0 && <span className={requirementStyles.badge}
              aria-label={t("solFacts.count", { n: requiredCount })}>{t("solFacts.badge", { n: requiredCount })}</span>}
          </button>
        );
      })}
    </div>
  );
}
